import {
  ApiError, Button, Card, ErrorState, Notice, Row, Stack, TenantIdentityFields, WEEKDAYS, identityBody, identityValuesOf, useApi, useI18n,
  type TenantSettingsResponse,
} from '@daghfal/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/**
 * Who the nursery is (C7, GAPS 3.9): its names, address, contact, colour and weekly days off, which its own
 * administration corrects here rather than asking the platform. The entry link, the timezone and the currency stay
 * with the platform, because changing them reaches links already sent and amounts already written.
 */
export function IdentityCard({ tenant }: { tenant: TenantSettingsResponse }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, formatPhone } = useI18n();
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState(() => identityValuesOf(tenant));

  const save = useMutation({
    mutationFn: () => api.updateCurrentTenant(identityBody(values)),
    onSuccess: async () => {
      // The shell's name and every screen that counts weekdays read the tenant record.
      await queryClient.invalidateQueries({ queryKey: ['tenant', 'current'] });
      setEditing(false);
    },
  });

  const open = () => {
    setValues(identityValuesOf(tenant));
    save.reset();
    setEditing(true);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  if (!editing) {
    const weekend = WEEKDAYS.filter((day) => tenant.weekendDays.includes(day)).map((day) => t(`weekday.${day}`));
    return (
      <Card
        title={t('identity.title')}
        action={<Button variant="secondary" onClick={open}>{t('action.edit')}</Button>}
      >
        <Stack tight>
          {save.isSuccess && <Notice tone="success">{t('identity.saved')}</Notice>}
          <span>{tenant.nameAr}{tenant.nameEn ? ` · ${tenant.nameEn}` : ''}</span>
          {tenant.address && <span className="dg-card__hint">{tenant.address}</span>}
          <span className="dg-card__hint">
            {tenant.contactPhone ? formatPhone(tenant.contactPhone) : '—'} · {tenant.contactEmail ?? '—'}
          </span>
          <span className="dg-card__hint">
            {t('identity.weekend', { days: weekend.length > 0 ? weekend.join('، ') : '—' })}
          </span>
        </Stack>
      </Card>
    );
  }

  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : {};

  return (
    <Card title={t('identity.title')} hint={t('identity.hint')}>
      <form noValidate onSubmit={submit}>
        <Stack>
          <TenantIdentityFields value={values} onChange={setValues} errors={fieldErrors} />
          {save.isError && <ErrorState error={save.error} />}
          <Row>
            <Button type="submit" busy={save.isPending} busyLabel={t('action.saving')}>{t('action.save')}</Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}
